import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { TokenStorage, TicketsAPI, Ticket } from '../../services/api';
import ApiReferenceContent from '../../components/ApiReferenceContent';
import { EyeIcon, FileText } from 'lucide-react';
import '../../styles/DashboardPages.scss';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://code.mannaplag.mannaanalytics.com/api';
declare global { interface Window { Razorpay: any; } }

function loadRazorpay(): Promise<boolean> {
  return new Promise(resolve => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

async function apiFetch(endpoint: string, method = 'GET', body?: object) {
  const token = TokenStorage.getAccess();
  const res = await fetch(`${BASE_URL}${endpoint}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  return { ok: res.ok, data: await res.json() };
}

// Some endpoints (e.g. create-renewal-order) expect multipart/form-data,
// not JSON — don't set Content-Type manually here, the browser needs to
// add the multipart boundary itself.
async function apiFetchForm(endpoint: string, formData: FormData) {
  const token = TokenStorage.getAccess();
  const res = await fetch(`${BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  return { ok: res.ok, data: await res.json() };
}

// ── STATUS BADGE ─────────────────────────────────────────────
// function StatusBadge({ status }: { status: string }) {
//   const map: Record<string, { bg: string; text: string; label: string }> = {
//     active: { bg: '#f0fdf4', text: '#16a34a', label: 'Active' },
//     created: { bg: '#fffbeb', text: '#d97706', label: 'Pending' },
//     past_due: { bg: '#fef2f2', text: '#dc2626', label: 'Past Due' },
//     cancelled: { bg: '#f9fafb', text: '#6b7280', label: 'Cancelled' },
//     free: { bg: '#f0f4ff', text: '#1a3a8f', label: 'Free Plan' },
//   };
//   const s = map[status] || map.free;
//   return (
//     <span className="text-[11px] font-bold px-2.5 py-1 rounded-full"
//       style={{ background: s.bg, color: s.text }}>{s.label}</span>
//   );
// }

// ════════════════════════════════════════════════════════════
// SUBSCRIPTION PAGE
// ════════════════════════════════════════════════════════════
export function SubscriptionPage() {
  const [sub, setSub] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [msg, setMsg] = useState('');

  // ── Renew subscription ────────────────────────────────────
  const [showRenew, setShowRenew] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [confirmingRenew, setConfirmingRenew] = useState(false);
  const [pendingRenewal, setPendingRenewal] = useState<any>(null);
  const [renewError, setRenewError] = useState('');
  // True when the backend rejects create-renewal-order because the
  // subscription has already lapsed — renewal only works on an active
  // subscription; an expired one needs the normal subscribe flow instead.
  const [needsResubscribe, setNeedsResubscribe] = useState(false);

  const loadSub = () =>
    apiFetch('/payments/my-subscription/')
      .then(({ data }) => setSub(data))
      .finally(() => setLoading(false));

  useEffect(() => { loadSub(); }, []);

  const handleCancel = async () => {
    setCancelling(true);
    const { ok, data } = await apiFetch('/payments/cancel/', 'POST');
    if (ok) {
      setMsg('Subscription cancelled. You will retain access until the end of your billing period.');
      setShowCancel(false);
      setSub((s: any) => ({ ...s, status: 'cancelled' }));
    } else {
      setMsg(data.error || 'Cancellation failed.');
    }
    setCancelling(false);
  };

  const openRenew = () => {
    setPendingRenewal(null);
    setRenewError('');
    setNeedsResubscribe(false);
    setShowRenew(true);
  };

  const closeRenew = () => {
    if (renewing || confirmingRenew) return;
    setShowRenew(false);
    setPendingRenewal(null);
    setRenewError('');
    setNeedsResubscribe(false);
  };

  // Step 1 — create the renewal order on the backend.
  // POST /payments/create-renewal-order/  (multipart/form-data: pricing_id)
  const createRenewal = async () => {
    if (!sub) return;
    setRenewing(true);
    setRenewError('');
    try {
      const formData = new FormData();
      // pricing_id identifies the exact plan+billing-cycle record being
      // renewed — falls back to plan_id if the subscription payload
      // doesn't expose pricing_id separately.
      formData.append('pricing_id', String(sub.pricing_id ?? sub.plan_id));

      const { ok, data } = await apiFetchForm('/payments/create-renewal-order/', formData);
      if (ok) {
        setPendingRenewal(data);
      } else {
        // This endpoint returns errors under "detail", not "error"
        // (e.g. { "detail": "Current subscription has already expired;
        // this is a resubscribe, not a renewal..." }).
        const message: string = data?.detail || data?.error || 'Could not start renewal. Please try again.';
        if (/resubscribe/i.test(message)) {
          setNeedsResubscribe(true);
        }
        setRenewError(message);
      }
    } catch (e: any) {
      setRenewError(e.message || 'Could not start renewal. Please try again.');
    } finally {
      setRenewing(false);
    }
  };

  // Step 2 — send the Razorpay success payload to the backend for
  // signature verification and finalize the renewal.
  // Uses the same /payments/verify/ endpoint as BillingPage's handlePayNow.
  const confirmRenewal = async (razorpayResponse: {
    razorpay_payment_id: string;
    razorpay_order_id: string;
    razorpay_signature: string;
  }) => {
    setConfirmingRenew(true);
    const { ok, data } = await apiFetch('/payments/verify/', 'POST', {
      razorpay_order_id: razorpayResponse.razorpay_order_id,
      razorpay_payment_id: razorpayResponse.razorpay_payment_id,
      razorpay_signature: razorpayResponse.razorpay_signature,
      subscription_id: pendingRenewal?.subscription_id ?? pendingRenewal?.new_subscription_id,
    });
    if (ok) {
      setMsg('Your subscription has been renewed.');
      setShowRenew(false);
      setPendingRenewal(null);
      loadSub();
    } else {
      setRenewError(data?.error || 'Payment succeeded but confirmation failed. Please contact support.');
    }
    setConfirmingRenew(false);
  };

  const payWithRazorpay = async () => {
    if (!pendingRenewal) return;
    setRenewError('');
    const loaded = await loadRazorpay();
    if (!loaded) {
      setRenewError('Payment system failed to load. Check your connection and try again.');
      return;
    }
    if (!pendingRenewal.razorpay_key_id || !pendingRenewal.razorpay_order_id) {
      setRenewError('Razorpay order details are missing from the server response.');
      return;
    }
    const options = {
      key: pendingRenewal.razorpay_key_id,
      amount: pendingRenewal.total_amount,
      currency: pendingRenewal.currency || 'INR',
      name: 'Manna Plag',
      description: `${sub?.plan_name || 'Subscription'} — Renewal`,
      image: "https://mannaplag.mannaanalytics.com/manna-plag-favicon.png",
      order_id: pendingRenewal.razorpay_order_id,
      prefill: pendingRenewal.prefill,
      theme: { color: '#003c6c' },
      handler: (response: any) => { confirmRenewal(response); },
      modal: { ondismiss: () => { } },
    };
    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (resp: any) => {
      setRenewError(`Payment failed: ${resp?.error?.description || 'please retry.'}`);
    });
    rzp.open();
  };

  if (loading) return <PageLoading />;

  const isFree = /* !sub ||  */sub.plan_name === 'Free' || sub.plan_name === "Demo";
  const sym = sub?.currency_symbol || '₹';

  const billingCycle = sub?.billing_cycle || '';

  // const cycleText =
  //   billingCycle.charAt(0).toUpperCase() +
  //   billingCycle.slice(1).toLowerCase();

  const cycle =
    billingCycle === 'annual'
      ? '/year'
      : billingCycle === 'monthly'
        ? '/month'
        : '/Lifetime';
  return (
    <div className="flex-1 overflow-y-auto dpg-s1">
      <PageHeader title="Subscription" sub="Manage your plan and billing" />

      <div className="p-6 max-w-2xl space-y-4">

        {msg && (
          <div className="p-3 rounded-xl text-[12px] font-medium dpg-s2"
          >
            {msg}
          </div>
        )}

        {/* Current plan card */}
        <div className="rounded-xl overflow-hidden dpg-s3">
          <div className="p-5 dpg-s4">
            <div className="flex items-start justify-between">
              <div>
                <p className="dpg-s5">Current Plan</p>
                <p className="dpg-s6">{sub?.plan_name || 'Free'}</p>
                {!isFree && (
                  <p className="dpg-s7">
                    {sym}
                    {sub?.amount?.toLocaleString()}
                    {cycle}
                    {billingCycle !== 'lifetime' && <> · Renews {sub?.period_end || '—'}</>}
                  </p>
                )}
              </div>
              <span
                className="dpg-s8"
              >
                {sub?.plan_name === "Free" ? "Free Plan" : sub?.plan_name}
              </span>
            </div>

            {/* Quota pills */}
            <div className="flex gap-2 mt-4 flex-wrap">
              <span className="dpg-s9">
                {sub?.papers_per_month || 1} plagiarism/mo
              </span>
              <span className="dpg-s10">
                {sub?.ai_checks_per_month || 10} AI checks/mo
              </span>
            </div>
          </div>

          <div className="p-5 flex gap-3 flex-wrap">
            <Link to="/choose-plan" state={{ upgradeOnly: true }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-white text-[12px] font-black transition-opacity hover:opacity-90 dpg-s11"
            >
              <i className="ti ti-arrow-up-circle text-[13px]" />
              {/* {isFree ? 'Upgrade plan' : 'Change plan'} */}
              Upgrade plan
            </Link>
            {!isFree && (
              <button onClick={openRenew}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-[12px] font-black transition-colors dpg-s12"

                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#eff6ff'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#fff'; }}>
                <i className="ti ti-rotate-clockwise text-[13px]" />
                Renew subscription
              </button>
            )}
            {!isFree && sub?.status !== 'cancelled' && (
              <button onClick={() => setShowCancel(true)}
                className="px-4 py-2.5 rounded-lg text-[12px] font-semibold transition-colors dpg-s13"

                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#fef2f2'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#fff'; }}>
                Cancel subscription
              </button>
            )}
          </div>
        </div>

        {/* Period info */}
        {!isFree && (
          <div className="rounded-xl p-4 grid grid-cols-2 gap-4 dpg-s3">
            {[
              {
                label: 'Billing cycle',
                value:
                  billingCycle,
              },
              { label: 'Period start', value: sub?.period_start || '—' },
              { label: 'Period end', value: sub?.period_end || '—' },
              { label: 'Status', value: sub?.status ? sub.status.charAt(0).toUpperCase() + sub.status.slice(1) : '—' },
            ].map(r => (
              <div key={r.label}>
                <p className="dpg-s14">{r.label}</p>
                <p className="dpg-s15">{r.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Cancel confirmation modal */}
        {showCancel && (
          <div className="fixed inset-0 flex items-center justify-center z-50 dpg-s16"
          >
            <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-2xl">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4 mx-auto dpg-s17"
              >
                <i className="ti ti-alert-circle text-red-500 text-[20px]" />
              </div>
              <h3 className="text-[15px] font-black text-gray-900 text-center mb-2">Cancel subscription?</h3>
              <p className="text-[12px] text-gray-500 text-center font-medium mb-5 leading-relaxed">
                You'll retain access until the end of your current billing period. After that, you'll be moved to the Free plan.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setShowCancel(false)}
                  className="flex-1 py-2.5 rounded-lg text-[12px] font-bold text-gray-700 dpg-s18"
                >
                  Keep plan
                </button>
                <button onClick={handleCancel} disabled={cancelling}
                  className="flex-1 py-2.5 rounded-lg text-[12px] font-bold text-white disabled:opacity-60 dpg-s19"
                >
                  {cancelling ? 'Cancelling...' : 'Yes, cancel'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Renew subscription modal */}
        {showRenew && (
          <div className="fixed inset-0 flex items-center justify-center z-50 dpg-s16"
          >
            <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-2xl dpg-s20" >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-[15px] font-black text-gray-900">Renew subscription</h3>
                  <p className="text-[12px] text-gray-500 font-medium mt-0.5">{sub?.plan_name} plan</p>
                </div>
                <button onClick={closeRenew} disabled={renewing || confirmingRenew}
                  className="text-gray-400 hover:text-gray-600 text-[13px]">✕</button>
              </div>

              {renewError && (
                <div className="p-2.5 rounded-lg text-[11px] font-medium mb-4 dpg-s21"
                >
                  {renewError}
                </div>
              )}

              {!pendingRenewal ? (
                <>
                  <div className="rounded-xl p-4 mb-5 space-y-2 dpg-s22">
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] font-bold text-gray-500">Plan</span>
                      <span className="text-[13px] font-black text-gray-900">{sub?.plan_name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] font-bold text-gray-500">Amount</span>
                      <span className="text-[13px] font-black text-gray-900">{sym}{sub?.amount?.toLocaleString()}{cycle}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] font-bold text-gray-500">Current period end</span>
                      <span className="text-[13px] font-black text-gray-900">{sub?.period_end || '—'}</span>
                    </div>
                  </div>
                  <div className="flex gap-3  w-80 mx-auto">
                    <button onClick={closeRenew} disabled={renewing}
                      className="flex-1 py-2.5 rounded-lg text-[12px] font-bold text-gray-700 dpg-s18"
                    >
                      Cancel
                    </button>
                    {needsResubscribe ? (
                      <Link to="/choose-plan"
                        className="flex-1 py-2.5 rounded-lg text-[12px] font-bold text-white text-center dpg-s11"
                      >
                        Choose a plan
                      </Link>
                    ) : (
                      <button onClick={createRenewal} disabled={renewing}
                        className="flex-1 py-2.5 rounded-lg text-[12px] font-bold text-white disabled:opacity-60 dpg-s11"
                      >
                        {renewing ? 'Creating order...' : 'Continue'}
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="rounded-xl p-4 mb-5 space-y-2 dpg-s22">
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] font-bold text-gray-500">Order</span>
                      <span className="text-[13px] font-black text-gray-900">{pendingRenewal.order_code || pendingRenewal.order_id}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] font-bold text-gray-500">Amount</span>
                      <span className="text-[13px] font-black text-gray-900">
                        {pendingRenewal.currency}{' '}
                        {Number(pendingRenewal.total_amount ?? pendingRenewal.amount / 100).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] font-bold text-gray-500">New end date</span>
                      <span className="text-[13px] font-black text-gray-900">{pendingRenewal.new_end_date || '—'}</span>
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-500 font-medium mb-4 flex items-center gap-1.5">
                    You'll be redirected to
                    <img
                      src="https://razorpay.com/favicon.png"
                      alt="Razorpay"
                      className="h-3.5 w-3.5 inline-block"
                    />
                    <span className="font-bold text-gray-700">Razorpay</span>
                    to complete payment securely.
                  </p>
                  <div className="flex gap-3">
                    <button onClick={closeRenew} disabled={confirmingRenew}
                      className="flex-1 py-2.5 rounded-lg text-[12px] font-bold text-gray-700 dpg-s18"
                    >
                      Cancel
                    </button>
                    <button onClick={payWithRazorpay} disabled={confirmingRenew}
                      className="flex-1 py-2.5 rounded-lg text-[12px] font-bold text-white disabled:opacity-60 flex items-center justify-center gap-1.5 dpg-s11"
                    >
                      <i className="ti ti-credit-card text-[13px]" />
                      {confirmingRenew ? 'Confirming...' : 'Pay with Razorpay'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// BILLING PAGE
// ════════════════════════════════════════════════════════════
export function BillingPage() {
  const [sub, setSub] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/payments/my-subscription/')
      .then(({ data }) => setSub(data))
      .finally(() => setLoading(false));
  }, []);

  const handlePayNow = async () => {
    if (!sub) return;
    setPaying(true); setError('');
    try {
      const loaded = await loadRazorpay();
      if (!loaded) throw new Error('Payment system failed to load.');

      const { ok, data: orderData } = await apiFetch('/payments/create-order/', 'POST', {
        plan_id: sub.plan_id,
        currency_id: sub.currency_id || 1,
      });
      if (!ok) throw new Error(orderData.error || 'Could not create order.');

      // const token = TokenStorage.getAccess();
      const options = {
        key: orderData.razorpay_key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Manna Plag',
        description: `${orderData.plan_name} — Renewal`,
        order_id: orderData.order_id,
        prefill: orderData.prefill,
        theme: { color: '#003c6c ' },
        handler: async (response: any) => {
          const { ok: vOk, data: vData } = await apiFetch('/payments/verify/', 'POST', {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            subscription_id: orderData.subscription_id,
          });
          if (vOk) {
            window.location.reload();
          } else {
            setError(vData.error || 'Verification failed.');
            setPaying(false);
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      };
      new window.Razorpay(options).open();
    } catch (e: any) {
      setError(e.message);
      setPaying(false);
    }
  };

  const downloadInvoice = (payment: any) => {
    // Simple printable invoice
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`
      <html><head><title>Invoice — ${payment.payment_id}</title>
      <style>body{font-family:sans-serif;padding:40px;color:#111}
      .header{display:flex;justify-content:space-between;margin-bottom:40px}
      h1{color:#003c6c ;margin:0}table{width:100%;border-collapse:collapse}
      td,th{padding:12px;border-bottom:1px solid #eee;text-align:left}
      th{font-size:11px;color:#999;text-transform:uppercase;letter-spacing:.05em}
      .total{font-weight:900;font-size:16px}.badge{background:#f0fdf4;color:#16a34a;
      padding:3px 10px;border-radius:20px;font-size:11px;font-weight:700}</style>
      </head><body>
      <div class="header">
        <div><h1>Manna Plag</h1><p style="color:#666;margin:4px 0 0">Research Integrity Platform</p></div>
        <div style="text-align:right"><p style="font-weight:700">Invoice</p>
          <p style="color:#666;font-size:13px">${payment.date}</p>
          <span class="badge">Paid</span></div>
      </div>
      <table><thead><tr><th>Description</th><th>Amount</th></tr></thead>
      <tbody>
        <tr><td>${payment.plan_name} Plan — Monthly Subscription</td>
            <td>${payment.currency}${payment.amount}</td></tr>
        <tr><td class="total">Total</td><td class="total">${payment.currency}${payment.amount}</td></tr>
      </tbody></table>
      <p style="margin-top:40px;color:#999;font-size:11px">Payment ID: ${payment.payment_id}</p>
      <script>window.print();</script></body></html>
    `);
    w.document.close();
  };

  if (loading) return <PageLoading />;

  const payments = sub?.payments || [];
  const upcoming = sub?.upcoming_invoice;

  return (
    <div className="flex-1 overflow-y-auto dpg-s1">
      <PageHeader title="Billing" sub="Transaction history and invoices" />

      <div className="p-6 max-w-2xl space-y-4">

        {error && (
          <div className="p-3 rounded-xl text-[12px] font-medium dpg-s21"
          >
            {error}
          </div>
        )}

        {/* Upcoming invoice */}
        {upcoming && (
          <div className="rounded-xl p-5 dpg-s23">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <i className="ti ti-clock text-amber-500 text-[15px]" />
                  <p className="text-[12px] font-black text-amber-800">Upcoming Invoice</p>
                </div>
                <p className="text-[13px] font-semibold text-amber-700">
                  {upcoming.currency}{upcoming.amount} due {upcoming.due_date}
                </p>
                <p className="text-[11px] text-amber-600 font-medium mt-0.5">
                  {upcoming.days_remaining === 0 ? 'Due today' : `${upcoming.days_remaining} days remaining`}
                </p>
              </div>
              <button onClick={handlePayNow} disabled={paying}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-white text-[12px] font-black disabled:opacity-60 transition-opacity hover:opacity-90 dpg-s24"
              >
                <i className="ti ti-credit-card text-[13px]" />
                {paying ? 'Processing...' : 'Pay now'}
              </button>
            </div>
          </div>
        )}

        {/* Transaction history */}
        <div className="rounded-xl overflow-hidden dpg-s3">
          <div className="flex items-center justify-between px-5 py-3.5 dpg-s25"
          >
            <p className="text-[13px] font-black text-gray-900">Transaction History</p>
          </div>

          {payments.length !== 0 ? (
            <div className="py-14 text-center">
              <i className="ti ti-receipt text-gray-200 text-4xl block mb-3" />
              <p className="text-[13px] font-black text-gray-400">No transactions yet</p>
              <p className="text-[11px] text-gray-400 font-medium mt-1">Your invoices will appear here after your first payment.</p>
            </div>
          ) : (
            <>
              {/* Table header */}
              <div className="grid px-5 py-2.5 dpg-s26"
              >
                {['Description', 'Date', 'Amount', 'Invoice'].map((h, i) => (
                  <span key={h} className="text-[9px] font-black text-gray-400 uppercase tracking-widest"
                    style={{ textAlign: i >= 2 ? 'right' : 'left' }}>{h}</span>
                ))}
              </div>

              {payments.map((p: any, i: number) => (
                <div key={p.id}
                  className="grid px-5 py-4 items-center dpg-s27"
                  style={{ borderBottom: i < payments.length - 1 ? '1px solid #f0f4f8' : 'none' }}>
                  <div>
                    <p className="text-[12px] font-semibold text-gray-800">{p.plan_name} Plan</p>
                    <p className="text-[10px] text-gray-400 font-medium mt-0.5">{p.payment_id}</p>
                  </div>
                  <span className="text-[11px] text-gray-500 font-medium">{p.date}</span>
                  <span className="text-[12px] font-bold text-gray-800 dpg-s28">
                    {p.currency}{p.amount}
                  </span>
                  <div className="dpg-s28">
                    <button onClick={() => downloadInvoice(p)}
                      className="inline-flex items-center gap-1 text-[11px] font-bold transition-colors dpg-s29"

                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.textDecoration = 'underline'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.textDecoration = 'none'; }}>
                      <i className="ti ti-download text-[12px]" />
                      PDF
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// SHARED HELPERS
// ════════════════════════════════════════════════════════════
function PageHeader({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="flex items-center gap-3 px-6 py-4 dpg-s30"
    >
      <div>
        <h1 className="text-[15px] font-black text-gray-900 tracking-tight leading-none">{title}</h1>
        <p className="text-[11px] text-gray-400 font-medium mt-0.5">{sub}</p>
      </div>
    </div>
  );
}

function PageLoading() {
  return (
    <div className="flex-1 flex items-center justify-center dpg-s31">
      <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin dpg-s32"
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// STUB PAGES — preserved from original DashboardPages
// These keep App.tsx imports working without changes
// ════════════════════════════════════════════════════════════

export function MyReports() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = React.useState<'plagiarism' | 'ai'>('plagiarism');

  const [plagReports, setPlagReports] = React.useState<any[]>([]);
  const [aiReports, setAiReports] = React.useState<any[]>([]);
  const [loadingPlag, setLoadingPlag] = React.useState(true);
  const [loadingAi, setLoadingAi] = React.useState(true);

  const [plagPage, setPlagPage] = React.useState(1);
  const [aiPage, setAiPage] = React.useState(1);
  const PER_PAGE = 10;

  React.useEffect(() => {
    apiFetch('/submissions/reports/')
      .then(({ data }) => {
        const items = (Array.isArray(data) ? data : data?.results || [])
          .map((r: any) => ({ ...r, type: 'plagiarism', date: r.submitted_at || r.generated_at }))
          .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
        setPlagReports(items);
      })
      .catch(() => { })
      .finally(() => setLoadingPlag(false));

    apiFetch('/checker/list/')
      .then(({ data }) => {
        const items = (Array.isArray(data) ? data : data?.results || [])
          .filter((r: any) => r.status === 'complete')
          .map((r: any) => ({ ...r, type: 'ai', overall_similarity: r.overall_similarity ?? 0, date: r.submitted_at }))
          .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
        setAiReports(items);
      })
      .catch(() => { })
      .finally(() => setLoadingAi(false));
  }, []);

  function scoreStyle(s: number) {
    if (s > 40) return { bg: '#fef2f2', text: '#e74c3c' };
    if (s > 20) return { bg: '#fffbeb', text: '#f39c12' };
    if (s > 10) return { bg: '#fefce8', text: '#f1c40f' };
    return { bg: '#f0fdf4', text: '#27ae60' };
  }

  function aiScoreStyle(s: number) {
    if (s > 60) return { bg: '#fef2f2', text: '#dc2626' };
    if (s > 30) return { bg: '#fffbeb', text: '#d97706' };
    if (s > 10) return { bg: 'rgba(14,165,233,0.12)', text: '#0ea5e9' };
    return { bg: '#f0fdf4', text: '#16a34a' };
  }

  const isAi = activeTab === 'ai';
  const reports = isAi ? aiReports : plagReports;
  const loading = isAi ? loadingAi : loadingPlag;
  const page = isAi ? aiPage : plagPage;
  const setPage = isAi ? setAiPage : setPlagPage;

  const totalPages = Math.max(1, Math.ceil(reports.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * PER_PAGE;
  const pageItems = reports.slice(startIdx, startIdx + PER_PAGE);

  function TabButton({ id, label, count }: { id: 'plagiarism' | 'ai'; label: string; count: number }) {
    const active = activeTab === id;
    return (
      <button
        onClick={() => setActiveTab(id)}
        className="dpg-s33" style={{ borderBottom: active ? '2px solid #003c6c' : '2px solid transparent', color: active ? '#003c6c' : '#9ca3af' }}
      >
        {label} <span className="dpg-s34">({count})</span>
      </button>
    );
  }

  function Pagination() {
    if (totalPages <= 1) return null;
    return (
      <div className="dpg-s35">
        <button
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="dpg-s36" style={{ color: currentPage === 1 ? '#d1d5db' : '#374151', cursor: currentPage === 1 ? 'default' : 'pointer' }}
        >
          ‹
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
          <button
            key={p}
            onClick={() => setPage(p)}
            className="dpg-s37" style={{ border: '1px solid ' + (p === currentPage ? '#003c6c' : '#e8edf4'), background: p === currentPage ? '#003c6c' : '#fff', color: p === currentPage ? '#fff' : '#374151' }}
          >
            {p}
          </button>
        ))}
        <button
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className="dpg-s36" style={{ color: currentPage === totalPages ? '#d1d5db' : '#374151', cursor: currentPage === totalPages ? 'default' : 'pointer' }}
        >
          ›
        </button>
      </div>
    );
  }

  return (
    <div className="dpg-s38">
      <div className="dpg-s39">
        <div>
          <p className="dpg-s40">My Reports</p>
          <p className="dpg-s41">All your plagiarism and AI check reports</p>
        </div>
      </div>

      <div className="dpg-s42">
        <button
          onClick={() => navigate(-1)}
          className="dpg-s43"
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#003c6c'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#6b7280'; }}
        >
          ← Back
        </button>
      </div>

      <div className="dpg-s44">
        <div className="dpg-s45">
          <TabButton id="plagiarism" label="Plagiarism" count={plagReports.length} />
          <TabButton id="ai" label="AI check" count={aiReports.length} />
        </div>
      </div>

      <div className="dpg-s46">
        {loading ? (
          <div className="dpg-s47">
            <div className="dpg-s48" />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        ) : reports.length === 0 ? (
          <div className="dpg-s49">
            <i className="ti ti-file-description dpg-s50" />
            <p className="dpg-s51">No reports yet</p>
            <p className="dpg-s52">Submit a manuscript to generate your first report.</p>
            <Link to="/dashboard/ai-check" className="dpg-s53">
              New check
            </Link>
          </div>
        ) : (
          <div className="dpg-s54">
            <div className="dpg-s55">
              {['SI', 'Title', 'Date', 'Score', 'Action'].map((h, i) => (
                <span key={h} className="dpg-s56" style={{ textAlign: i >= 2 ? 'center' : 'left' }}>{h}</span>
              ))}
            </div>
            {pageItems.map((r, i) => {
              const sc = isAi ? aiScoreStyle(r.overall_similarity ?? 0) : scoreStyle(r.overall_similarity ?? 0);
              const path = `/reports/${r.id}?type=${r.type}`;
              const siNo = startIdx + i + 1;
              return (
                <div key={r.id}
                  onClick={() => navigate(path)}
                  className="dpg-s57" style={{ borderBottom: i < pageItems.length - 1 ? '1px solid #f8fafc' : 'none' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#f8fafc'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <span className="dpg-s58">{siNo}</span>
                  <div>
                    <p className="dpg-s59">{r.title || r.file_name}</p>
                    <p className="dpg-s60">{r.file_name}</p>
                  </div>
                  <span className="dpg-s61">
                    {r.date ? new Date(r.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  </span>
                  <div className="dpg-s62">
                    <span className="dpg-s63" style={{ background: sc.bg, color: sc.text }}>
                      {r.overall_similarity ?? 0}%
                    </span>
                  </div>
                  <div className="dpg-s62">
                    <button className='view-btn'>View</button>
                  </div>
                </div>
              );
            })}
            <Pagination />
          </div>
        )}
      </div>
    </div>
  );
}

export function SubmissionHistory() {
  return (
    <div className="dpg-s38">
      <div className="dpg-s64">
        <p className="dpg-s40">Submission History</p>
        <p className="dpg-s41">All manuscripts you have submitted</p>
      </div>
      <div className="dpg-s65">
        <div className="dpg-s66">
          <i className="ti ti-history dpg-s50" />
          <p className="dpg-s51">No submissions yet</p>
          <Link to="/dashboard/plagiarism" className="dpg-s67">
            Submit a manuscript
          </Link>
        </div>
      </div>
    </div>
  );
}

interface MonthData {
  month: string;
  year: string;
  month_key: string;
  is_current: boolean;
  plag_count: number;
  plag_quota: number;
  plag_remaining: number | null;
  ai_count: number;
  ai_quota: number;
  avg_similarity: number;
  avg_ai_similarity: number;   // NEW — average AI score for the month, from API
  quota_full: boolean;
}

interface UsageData {
  plan_name: string;
  papers_per_month: number;
  ai_per_month: number;
  months: MonthData[];
}

function scoreStyle(score: number) {
  if (score > 15) return { background: '#fef2f2', color: '#dc2626' };
  if (score > 8) return { background: '#fffbeb', color: '#d97706' };
  if (score === 0) return { background: '#f8fafc', color: '#9ca3af' };
  return { background: '#f0fdf4', color: '#16a34a' };
}

export function UsagePage() {
  const navigate = useNavigate();
  const [data, setData] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [months, /* setMonths */] = useState<6 | 12>(12);

  useEffect(() => {
    setLoading(true);
    apiFetch(`/submissions/usage-history/?months=${months}`)
      .then(({ data }) => setData(data))
      .catch(() => setError('Could not load usage data.'))
      .finally(() => setLoading(false));
  }, [months]);

  const GRID_COLS = '100px 1fr 1fr 80px 80px 90px 70px';

  // Only keep the current month and months that actually have activity

  const visibleMonths = data
    ? data.months.filter(m => m.is_current || m.plag_count > 0 || m.ai_count > 0)
    : [];
  return (
    <div className="dpg-s38">

      {/* Page header */}
      <div className="dpg-s68">
        <div>
          <p className="dpg-s69">Usage & History</p>
          <p className="dpg-s70">Month-on-month quota and similarity trends</p>
        </div>
        {/* <div className="dpg-s71">
          {([6, 12] as const).map(n => (
            <button key={n} onClick={() => setMonths(n)}
              className="dpg-s72" style={{ background: months === n ? '#003c6c ' : '#fff', color: months === n ? '#fff' : '#374151' }}>
              {n} months
            </button>
          ))}
        </div> */}
      </div>

      <div className="dpg-s73">

        {loading && (
          <div className="dpg-s74">
            <div className="dpg-s75" />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}

        {error && (
          <div className="dpg-s76">
            {error} <button onClick={() => window.location.reload()} className="dpg-s77">Retry</button>
          </div>
        )}

        {!loading && !error && data && (
          <div className="dpg-s78">

            {/* Table header */}
            <div className="dpg-s79" style={{ gridTemplateColumns: GRID_COLS }}>
              {['Month', 'Plagiarism', 'AI checks', 'Avg score', 'AI avg score', 'Quota',].map((h, i) => (
                <span key={h} className="dpg-s80" style={{ textAlign: i >= 3 ? 'center' : 'left' }}>
                  {h}
                </span>
              ))}
            </div>

            {visibleMonths.map((m, i) => {
              const plagQuota = m.plag_quota || data.papers_per_month;
              const aiQuota = m.ai_quota || data.ai_per_month;
              const isLast = i === visibleMonths.length - 1;

              let badgeBg = '#f8fafc', badgeColor = '#6b7280', badgeText = '';
              if (m.is_current) {
                if (m.quota_full) {
                  badgeBg = '#fef2f2'; badgeColor = '#dc2626'; badgeText = 'Full ⚠';
                } else {
                  badgeBg = '#f0fdf4'; badgeColor = '#16a34a';
                  badgeText = `${m.plag_remaining} left`;
                }
              } else {
                if (m.quota_full) {
                  badgeBg = '#fef2f2'; badgeColor = '#dc2626'; badgeText = 'Full ⚠';
                } else if (m.plag_count === 0) {
                  badgeText = 'Unused';
                } else {
                  badgeBg = '#f0fdf4'; badgeColor = '#16a34a';
                  badgeText = `${plagQuota - m.plag_count} left`;
                }
              }

              const hasActivity = m.plag_count > 0 || m.ai_count > 0;

              return (
                <div key={m.month_key}
                  className="dpg-s81" style={{ gridTemplateColumns: GRID_COLS, borderBottom: isLast ? 'none' : '1px solid #f8fafc', background: m.is_current ? '#eef3fb' : 'transparent' }}
                  onMouseEnter={e => { if (!m.is_current) (e.currentTarget as HTMLElement).style.background = '#f8fafc'; }}
                  onMouseLeave={e => { if (!m.is_current) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <div>
                    <p className="dpg-s82">{m.month}</p>
                    <p className="dpg-s60">
                      {m.year}{m.is_current ? ' · now' : ''}
                    </p>
                  </div>

                  <div className="dpg-s83">
                    <span className="dpg-s84" style={{ color: m.quota_full ? '#dc2626' : '#003c6c' }}>
                      {m.plag_count}
                    </span>
                    <span className="dpg-s85">
                      / {plagQuota} check{plagQuota !== 1 ? 's' : ''}
                    </span>
                  </div>

                  <div className="dpg-s83">
                    <span className="dpg-s86">
                      {m.ai_count}
                    </span>
                    <span className="dpg-s85">
                      / {aiQuota} checks
                    </span>
                  </div>

                  <div className="dpg-s62">
                    {m.plag_count > 0 ? (
                      <span style={{ fontSize: 12, fontWeight: 900, padding: '4px 10px', borderRadius: 6, display: 'inline-block', ...scoreStyle(m.avg_similarity) }}>
                        {m.avg_similarity}%
                      </span>
                    ) : (
                      <span className="dpg-s87">—</span>
                    )}
                  </div>

                  <div className="dpg-s62">
                    {m.ai_count > 0 ? (
                      <span style={{ fontSize: 12, fontWeight: 900, padding: '4px 10px', borderRadius: 6, display: 'inline-block', ...scoreStyle(m.avg_ai_similarity ?? 0) }}>
                        {m.avg_ai_similarity ?? 0}%
                      </span>
                    ) : (
                      <span className="dpg-s87">—</span>
                    )}
                  </div>

                  <div className="dpg-s62">
                    <span className="dpg-s88" style={{ background: badgeBg, color: badgeColor }}>
                      {badgeText || '—'}
                    </span>
                  </div>

                  <div className="dpg-s62">
                    {hasActivity ? (
                      // <Link to={`/dashboard/reports?month=${m.month_key}`}
                      //   style={{ fontSize: 10, fontWeight: 700, padding: '5px 10px', borderRadius: 6, background: '#003c6c', color: '#fff', textDecoration: 'none', display: 'inline-block' }}>
                      //   View
                      // </Link>
                      <button
                        onClick={() => navigate(`/dashboard/reports?month=${m.month_key}`)}
                        className="view-btn"
                      >
                        <FileText size={12} />
                        View
                      </button>
                    ) : (
                      <span className="dpg-s89">—</span>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Empty state — checks visibleMonths, not data.months */}
            {visibleMonths.every(m => m.plag_count === 0 && m.ai_count === 0) && (
              <div className="dpg-s90">
                <p className="dpg-s91">No checks yet</p>
                <p className="dpg-s92">Submit your first manuscript to start tracking usage.</p>
                <Link to="/dashboard/plagiarism" className="dpg-s67">
                  New check
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
export function APIKeysPage() {
  return (
    <div className="dpg-s38">
      <div className="dpg-s64">
        <p className="dpg-s40">API Keys</p>
        <p className="dpg-s41">Manage your API credentials</p>
      </div>
      <div className="dpg-s93">
        <div className="dpg-s94">
          <i className="ti ti-lock dpg-s95" />
          <div>
            <p className="dpg-s96">API key generation requires a Publisher plan</p>
            <p className="dpg-s97">Upgrade to Enterprise API to generate and manage API keys.</p>
            <Link to="/choose-plan" state={{ upgradeOnly: true }} className="dpg-s98">Upgrade now →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export function APIUsagePage() {
  return (
    <div className="dpg-s38">
      <div className="dpg-s64">
        <p className="dpg-s40">API Usage</p>
        <p className="dpg-s41">Monitor your API consumption</p>
      </div>
      <div className="dpg-s65">
        <div className="dpg-s66">
          <i className="ti ti-activity dpg-s50" />
          <p className="dpg-s99">No API usage yet</p>
        </div>
      </div>
    </div>
  );
}

export function APIDocsPage() {
  return (
    <div className="dpg-s38">
      <div className="dpg-s64">
        <p className="dpg-s40">API Documentation</p>
        <p className="dpg-s41">Integration guide and API reference</p>
      </div>
      <div className="dpg-s65">
        <ApiReferenceContent />
      </div>
    </div>
  );
}

const TICKET_CATEGORIES = ['Billing', 'Technical', 'Account', 'Report issue', 'Feature request', 'Other'];
const TICKET_PRIORITIES = ['Low', 'Medium', 'High'];
const REOPENABLE_STATUSES = ['resolved', 'closed'];

function TicketStatusBadge({ status }: { status: string }) {
  const s = (status || '').toLowerCase();
  const map: Record<string, { bg: string; text: string }> = {
    open: { bg: '#eff6ff', text: '#1d4ed8' },
    pending: { bg: '#fffbeb', text: '#d97706' },
    resolved: { bg: '#f0fdf4', text: '#16a34a' },
    closed: { bg: '#f3f4f6', text: '#6b7280' },
  };
  const c = map[s] || { bg: '#f3f4f6', text: '#6b7280' };
  return (
    <span className="dpg-s100" style={{ background: c.bg, color: c.text }}>
      {status || '—'}
    </span>
  );
}

function TicketPriorityBadge({ priority }: { priority: string }) {
  const p = (priority || '').toLowerCase();
  const map: Record<string, { bg: string; text: string }> = {
    high: { bg: '#fef2f2', text: '#dc2626' },
    medium: { bg: '#fffbeb', text: '#d97706' },
    low: { bg: '#f3f4f6', text: '#6b7280' },
  };
  const c = map[p] || { bg: '#f3f4f6', text: '#6b7280' };
  return (
    <span className="dpg-s101" style={{ background: c.bg, color: c.text }}>
      {priority || '—'}
    </span>
  );
}

function formatTicketDate(iso: string) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

// ── New ticket modal ─────────────────────────────────────────
function NewTicketModal({ onClose, onCreated }: { onClose: () => void; onCreated: (ticketNumber: string) => void }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.full_name || '',
    email: user?.email || '',
    subject: '',
    message: '',
    category: TICKET_CATEGORIES[0],
    priority: 'Medium',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const set = (k: string, v: string) => {
    setForm(f => ({ ...f, [k]: v }));
    // clear that field's error as soon as the user starts fixing it
    if (fieldErrors[k]) {
      setFieldErrors(fe => {
        const next = { ...fe };
        delete next[k];
        return next;
      });
    }
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = 'Name is required.';
    if (!form.email.trim()) errs.email = 'Email is required.';
    if (!form.subject.trim()) errs.subject = 'Subject is required.';
    if (!form.message.trim()) errs.message = 'Message is required.';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      setError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await TicketsAPI.create({ ...form, source: 'Website' });
      onCreated(res?.ticket_number);
    } catch (err: any) {
      setError(err.message || 'Could not create ticket.');
    } finally {
      setSubmitting(false);
    }
  };

  const errorInputStyle = (hasErr: boolean) => ({
    width: '100%',
    border: `1.5px solid ${hasErr ? '#fca5a5' : '#e5e7eb'}`,
    borderRadius: 8,
    padding: '9px 11px',
    fontSize: 13,
    outline: 'none',
  });

  const FieldError = ({ msg }: { msg?: string }) =>
    msg ? (
      <p className="dpg-s102">{msg}</p>
    ) : null;

  return (
    <div className="dpg-s103">
      <div className="dpg-s104">
        <div className="dpg-s105">
          <p className="dpg-s106">New support ticket</p>
          <button onClick={onClose} className="dpg-s107">×</button>
        </div>
        <form onSubmit={handleSubmit} className="dpg-s65" noValidate>
          {error && (
            <div className="dpg-s108">
              {error}
            </div>
          )}
          <div className="dpg-s109">
            <div>
              <label className="dpg-s110">Name</label>
              <input value={form.name} onChange={e => set('name', e.target.value)}
                style={errorInputStyle(!!fieldErrors.name)} />
              <FieldError msg={fieldErrors.name} />
            </div>
            <div>
              <label className="dpg-s110">Email</label>
              <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                style={errorInputStyle(!!fieldErrors.email)} />
              <FieldError msg={fieldErrors.email} />
            </div>
          </div>
          <div className="dpg-s111">
            <label className="dpg-s110">Subject</label>
            <input value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="Short summary of the issue"
              style={errorInputStyle(!!fieldErrors.subject)} />
            <FieldError msg={fieldErrors.subject} />
          </div>
          <div className="dpg-s109">
            <div>
              <label className="dpg-s110">Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)}
                className="dpg-s112">
                {TICKET_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="dpg-s110">Priority</label>
              <select value={form.priority} onChange={e => set('priority', e.target.value)}
                className="dpg-s112">
                {TICKET_PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div className="dpg-s113">
            <label className="dpg-s110">Message</label>
            <textarea value={form.message} onChange={e => set('message', e.target.value)} rows={4}
              placeholder="Describe the issue in detail…"
              style={{ ...errorInputStyle(!!fieldErrors.message), resize: 'none' }} />
            <FieldError msg={fieldErrors.message} />
          </div>
          <div className="dpg-s114">
            <button type="button" onClick={onClose}
              className="dpg-s115">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="dpg-s116" style={{ opacity: submitting ? 0.6 : 1 }}>
              {submitting ? 'Submitting…' : 'Submit ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Ticket detail modal ──────────────────────────────────────
function TicketDetailModal({ ticketId, onClose, onChanged }: { ticketId: number; onClose: () => void; onChanged: (t: Ticket) => void }) {
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reopening, setReopening] = useState(false);

  useEffect(() => {
    setLoading(true);
    TicketsAPI.detail(ticketId)
      .then(setTicket)
      .catch(err => setError(err.message || 'Could not load ticket.'))
      .finally(() => setLoading(false));
  }, [ticketId]);

  const handleReopen = async () => {
    setReopening(true);
    setError('');
    try {
      await TicketsAPI.reopen(ticketId);
      const refreshed = await TicketsAPI.detail(ticketId);
      setTicket(refreshed);
      onChanged(refreshed);
    } catch (err: any) {
      setError(err.message || 'Could not reopen ticket.');
    } finally {
      setReopening(false);
    }
  };

  const canReopen = ticket && REOPENABLE_STATUSES.includes((ticket.status || '').toLowerCase());

  return (
    <div className="dpg-s103">
      <div className="dpg-s117">
        <div className="dpg-s105">
          <p className="dpg-s106">
            {ticket ? ticket.ticket_number : 'Ticket detail'}
          </p>
          <button onClick={onClose} className="dpg-s107">×</button>
        </div>
        <div className="dpg-s65">
          {loading && (
            <div className="dpg-s118">
              <div className="dpg-s119" />
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          )}

          {error && !loading && (
            <div className="dpg-s108">
              {error}
            </div>
          )}

          {ticket && !loading && (
            <>
              <div className="dpg-s120">
                <TicketStatusBadge status={ticket.status} />
                <TicketPriorityBadge priority={ticket.priority} />
                {ticket.category && (
                  <span className="dpg-s121">{ticket.category}</span>
                )}
              </div>

              <p className="dpg-s122">{ticket.subject}</p>
              <p className="dpg-s92">
                From {ticket.name} ({ticket.email}) · Opened {formatTicketDate(ticket.created_at)}
              </p>

              <div className="dpg-s123">
                <p className="dpg-s124">{ticket.message}</p>
              </div>

              {ticket.admin_reply && (
                <div className="dpg-s125">
                  <p className="dpg-s126">
                    Support team reply
                  </p>
                  <p className="dpg-s127">{ticket.admin_reply}</p>
                </div>
              )}

              <div className="dpg-s128">
                <div>
                  <p className="dpg-s129">Created</p>
                  <p className="dpg-s130">{formatTicketDate(ticket.created_at)}</p>
                </div>
                <div>
                  <p className="dpg-s129">Last updated</p>
                  <p className="dpg-s130">{formatTicketDate(ticket.updated_at)}</p>
                </div>
              </div>

              {canReopen && (
                <button onClick={handleReopen} disabled={reopening}
                  className="dpg-s131" style={{ opacity: reopening ? 0.6 : 1 }}>
                  {reopening ? 'Reopening…' : 'Reopen ticket'}
                </button>
              )}
              {!canReopen && (
                <p className="dpg-s132">
                  Our team typically responds within 1 business day.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function TicketsPage() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [activeTicketId, setActiveTicketId] = useState<number | null>(null);
  const [toast, setToast] = useState('');

  const loadTickets = () => {
    setLoading(true);
    TicketsAPI.my()
      .then(data => setTickets(Array.isArray(data) ? data : []))
      .catch(err => setError(err.message || 'Could not load tickets.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadTickets(); }, []);

  const handleCreated = (ticketNumber: string) => {
    setShowNew(false);
    setToast(`Ticket ${ticketNumber || ''} created successfully.`);
    loadTickets();
    setTimeout(() => setToast(''), 4000);
  };

  const handleChanged = (updated: Ticket) => {
    setTickets(prev => prev.map(t => t.ticket_id === updated.ticket_id ? updated : t));
  };

  return (
    <div className="dpg-s38">
      <div className="dpg-s133">
        <div>
          <p className="dpg-s40">Support Tickets</p>
          <p className="dpg-s41">Get help from our support team</p>
        </div>
        <button onClick={() => setShowNew(true)}
          className="dpg-s134">
          + New ticket
        </button>
      </div>

      {toast && (
        <div className="dpg-s135">
          {toast}
        </div>
      )}

      <div className="dpg-s65">
        {loading && (
          <div className="dpg-s118">
            <div className="dpg-s48" />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}

        {!loading && error && (
          <div className="dpg-s66">
            <p className="dpg-s136">{error}</p>
          </div>
        )}

        {!loading && !error && tickets.length === 0 && (
          <div className="dpg-s66">
            <i className="ti ti-headset dpg-s50" />
            <p className="dpg-s51">No tickets yet</p>
            <p className="dpg-s52">Our team typically responds within 1 business day.</p>
            <button onClick={() => setShowNew(true)}
              className="dpg-s137">
              Open a ticket
            </button>
          </div>
        )}

        {!loading && !error && tickets.length > 0 && (
          <div className="dpg-s78">
            <div className="dpg-s138">
              {['Ticket #', 'Subject', 'Category', 'Priority', 'Status', 'Updated', 'Action'].map((h, i) => (
                <span key={h} className="dpg-s56" style={{ textAlign: i === 6 ? 'center' : 'left' }}>{h}</span>
              ))}
            </div>
            {tickets.map((t, i) => (
              <div key={t.ticket_id}
                onClick={() => setActiveTicketId(t.ticket_id)}
                className="dpg-s139" style={{ borderBottom: i < tickets.length - 1 ? '1px solid #f8fafc' : 'none' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#f8fafc'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                <span className="dpg-s140">{t.ticket_number}</span>
                <span className="dpg-s59">{t.subject}</span>
                <span className="dpg-s141">{t.category || '—'}</span>
                <TicketPriorityBadge priority={t.priority} />
                <TicketStatusBadge status={t.status} />
                <span className="dpg-s142">{formatTicketDate(t.updated_at)}</span>
                <div className="dpg-s62">
                  <button
                    onClick={(e) => { e.stopPropagation(); setActiveTicketId(t.ticket_id); }}
                    title="View ticket"
                    className="dpg-s143">
                    <EyeIcon className="dpg-s144" />
                    View
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showNew && <NewTicketModal onClose={() => setShowNew(false)} onCreated={handleCreated} />}
      {activeTicketId !== null && (
        <TicketDetailModal ticketId={activeTicketId} onClose={() => setActiveTicketId(null)} onChanged={handleChanged} />
      )}
    </div>
  );
}

export function InvoicesPage() {
  // Redirect to new BillingPage — same content
  return <BillingPage />;
}

export function SettingsPage() {
  return (
    <div className="dpg-s38">
      <div className="dpg-s64">
        <p className="dpg-s40">Settings</p>
        <p className="dpg-s41">Manage your account preferences</p>
      </div>
      <div className="dpg-s93">
        {[
          { title: 'Email notifications', desc: 'Receive email when your report is ready', enabled: true },
          { title: 'Marketing emails', desc: 'Receive updates, tips and product news', enabled: false },
          { title: 'Weekly summary', desc: 'Get a weekly summary of your checks', enabled: true },
        ].map(s => (
          <div key={s.title} className="dpg-s145">
            <div>
              <p className="dpg-s146">{s.title}</p>
              <p className="dpg-s41">{s.desc}</p>
            </div>
            <div className="dpg-s147" style={{ background: s.enabled ? '#003c6c ' : '#e5e7eb' }}>
              <div className="dpg-s148" style={{ left: s.enabled ? 21 : 3 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ChangePassword() {
  return (
    <div className="dpg-s38">
      <div className="dpg-s64">
        <p className="dpg-s40">Change Password</p>
        <p className="dpg-s41">Update your account password</p>
      </div>
      <div className="dpg-s149">
        <div className="dpg-s150">
          {['Current password', 'New password', 'Confirm new password'].map(label => (
            <div key={label} className="dpg-s151">
              <label className="dpg-s152">{label}</label>
              <input type="password" className="dpg-s153" />
            </div>
          ))}
          <button className="dpg-s154">
            Update password
          </button>
        </div>
      </div>
    </div>
  );
}